import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useLocation } from "react-router-dom";
import Breadcrumb from "../../components/navigation/Bredcrumbs";
import DetailTopPart from "../../components/popups/DetailTopPart";
import EmployeePayrollTable from "../../components/tables/EmployePayrollTable";
import FilterTopBar from "../../components/navigation/FilterTopBar";
import DDConfirmationPopup from "../../components/popups/DDConfirmationPopup";
import TaxPaymentModal from "../../components/popups/TaxPaymentModal";
import user_img_1 from "../../assets/images/User.png";
import calendar_icon from "../../assets/images/calendar_icon.svg";
import { useAppSelector } from "../../hooks/useAppSelector";
import dollar_white from "../../assets/images/dollar_white.svg";
import export_icon from "../../assets/images/export_icon.svg";
import TopHeadding from "../../components/layout/TopHeadding";
import money from "../../assets/images/money.svg";
import calculator_money_icon from "../../assets/images/Calculator_Dollar_2.svg";
import taxpay from "../../assets/images/taxpayicon.svg";
import {
  fetchEmployeesPayroll,
  generatePayCheck,
  runPayrollCalulation,
  exportPayrollExcel,
  updateEmployeesWorkDetails,
  selectUpdatingEmployeeIds,
} from "../../store/slices/payrollSlice";
import { formatDate } from "../../utils/dateUtils";

import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const EMPLOYEE_PAYROLL_KEY = "EmployeeDashboard";

const EmployePayroll = () => {
  const dispatch = useDispatch();
  const { id: paramCompanyId } = useParams();

  const { state } = useLocation();

  const { user, role } = useAppSelector((s) => s.user);

  const storeCompanyId = user?.id;

  const allowedRoles = ["SuperAdmin", "Client"];

  const companyId = allowedRoles.includes(role)
    ? paramCompanyId
    : storeCompanyId;

  const showExportButton = allowedRoles.includes(role);

  const company = state?.companyData;

  const getCurrentWeekRange = () => {
    const today = new Date();
    const day = today.getDay();
    const diff = day === 0 ? -6 : 1 - day;

    const start = new Date(today);
    start.setDate(today.getDate() + diff);

    const end = new Date(start);
    end.setDate(start.getDate() + 6);

    return [start, end];
  };

  const [dateRange, setDateRange] = useState(() => getCurrentWeekRange());
  const [startDate, endDate] = dateRange;

  const [search, setSearch] = useState("");

  const [filters, setFilters] = useState({ payRange: [0, 1000000] });

  const [page, setPage] = useState(1);
  const pageSize = 10;

  const [showPayrollModal, setShowPayrollModal] = useState(false);

  const [showTaxModal, setShowTaxModal] = useState(false);

  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const payrollHeaders = useSelector(
    (state) => state.payroll.listDataByKey[EMPLOYEE_PAYROLL_KEY] || [],
  );

  const loading = useSelector(
    (state) => state.payroll.listLoadingByKey[EMPLOYEE_PAYROLL_KEY],
  );

  const generatingPaycheckId = useSelector(
    (state) => state.payroll.generatingPaycheckId,
  );

  const calculatingPayroll = useSelector(
    (state) => state.payroll.entityLoadingByKey[`runPayroll-${companyId}`],
  );

  const updatingEmployeeIds = useSelector(selectUpdatingEmployeeIds);

  const exportingPayrollId = useSelector(
    (state) => state.payroll.exportingPayrollId,
  );

  const totalRecords = useSelector(
    (state) => state.payroll.totalByKey[EMPLOYEE_PAYROLL_KEY] || 0,
  );

  const toDateOnly = (date) => {
    if (!date) return null;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const BASE_URL = process.env.REACT_APP_IMAGE_BASE_URL || "";

  const formatMoney = (v) =>
    v !== null && v !== undefined ? `$${Number(v).toLocaleString()}` : "-";

  useEffect(() => {
    if (!companyId || !startDate || !endDate) return;

    const timeout = setTimeout(() => {
      const params = {
        companyId,
        startDate: toDateOnly(startDate),
        endDate: toDateOnly(endDate),
        pageIndex: page - 1,
        pageSize: pageSize,
        minPay: filters.payRange[0],
        maxPay: filters.payRange[1],
      };
      if (search) {
        params.search = search;
      }

      dispatch(
        fetchEmployeesPayroll({
          key: EMPLOYEE_PAYROLL_KEY,
          params,
        }),
      );
    }, 400);

    return () => clearTimeout(timeout);
  }, [companyId, startDate, endDate, page, search, filters, dispatch]);

  const handleGeneratePaycheck = async (payrollEmployeeId) => {
    try {
      const action = await dispatch(generatePayCheck(payrollEmployeeId));

      if (generatePayCheck.fulfilled.match(action)) {
        const { file } = action.payload;

        const blob = new Blob([file], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);

        const a = document.createElement("a");
        a.href = url;
        a.download = `Paycheck_${payrollEmployeeId}.pdf`;
        a.click();

        window.URL.revokeObjectURL(url);
      } else {
        // Handle rejection
        setErrorMessage("Failed to generate paycheck. Please try again.");
        setShowErrorPopup(true);
      }
    } catch (error) {
      setErrorMessage(
        "An error occurred while generating the paycheck. Please try again.",
      );
      setShowErrorPopup(true);
    }
  };

  const handlePayrollModalOpen = () => {
    setShowPayrollModal(true);
  };

  const handlePayrollModalClose = () => setShowPayrollModal(false);

  const handleUpdateWorkDetails = async (headerId, dto) => {
    const employeeId = dto[0]?.PayrollEmployeeId;
    try {
      await dispatch(
        updateEmployeesWorkDetails({
          key: `update-workDetail${employeeId}`,
          headerId,
          dto,
        }),
      ).unwrap();
      const params = {
        companyId,
        startDate: toDateOnly(startDate),
        endDate: toDateOnly(endDate),
        pageIndex: page - 1,
        pageSize: pageSize,
        minPay: filters.payRange[0],
        maxPay: filters.payRange[1],
        status: filters.status,
      };
      if (search) {
        params.search = search;
      }
      dispatch(
        fetchEmployeesPayroll({
          key: EMPLOYEE_PAYROLL_KEY,
          params,
        }),
      );
    } catch (error) {
      setErrorMessage("Failed to update work details. Please try again.");
      setShowErrorPopup(true);
    }
  };

  const employees = useMemo(() => {
    return payrollHeaders.flatMap((header) =>
      header.payrollEmployeeDetails.map((emp) => ({
        payrollEmployeeId: emp.id,
        name: emp.employeeName,
        image: emp.profile ? BASE_URL + emp.profile : user_img_1,
        address: emp.address,

        deductions: emp.deductions,
        taxes: emp.taxes,

        fixedSalary: emp.fixedSalary,
        incentive: emp.commissionBonus,

        totalHours: emp.totalHours,
        regularHours: emp.regularHours,
        overtime: emp.overtimeHours,
        rate: emp.rate,

        gross: formatMoney(emp.grossPay),
        afterPreTax: formatMoney(emp.grossAfterPreTax),
        totalTax: formatMoney(emp.totalTax),
        netPay: formatMoney(emp.netPay),
        netPayAfterWH: formatMoney(emp.netAfterAdditionalWH),

        accountType: emp.accountType ?? "DD",
      })),
    );
  }, [payrollHeaders, BASE_URL]);

  const payrollPeriod = payrollHeaders?.[0]
    ? `${formatDate(payrollHeaders[0].payrollFrom)} - ${formatDate(
        payrollHeaders[0].payrollTo,
      )}`
    : "";

  const payrollStatus = payrollHeaders?.[0]?.status || "";

  const breadcrumbItems = [
    { label: "Companies", link: "/companies" },
    {
      label: company?.name || company?.companyName || "Company",
      link: `/companydetailpage/${companyId}`,
    },
    { label: "Payroll", active: true },
  ];

  if (!company && !companyId) {
    return <div className="p-4 text-center">Invalid Company</div>;
  }

  const handleDateChange = ({ startDate, endDate }) => {
    setDateRange([startDate, endDate]);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const payButton =
    payrollStatus === "Paid"
      ? {
          text: "Paid",
          className: "btn_primary_light_blue",
          disabled: true,
          icon: money,
          onClick: () => {},
        }
      : {
          text: "Pay DD",
          className: "btn_primary",
          icon: dollar_white,
          onClick: handlePayrollModalOpen,
        };

  return (
    <div className="company_detail">
      {allowedRoles.includes(role) && <Breadcrumb items={breadcrumbItems} />}
      {allowedRoles.includes(role) && (
        <DetailTopPart
          client={{
            name: company?.name || company?.companyName,
            email: company?.email,
            phone: company?.phone,
            address: company?.address,
            image: company?.image ? BASE_URL + company.image : user_img_1,
          }}
          labels={{
            email: "Email",
            phone: "Contact",
            address: "Address",
          }}
          extraLeftText={payrollPeriod}
          className="compnay_detail_top"
        />
      )}

      {!allowedRoles.includes(role) && (
        <TopHeadding
          title="Payroll"
          subtitle=""
          showDatepicker={false}
          buttons={[
            {
              text:
                exportingPayrollId === payrollHeaders[0]?.id
                  ? "Exporting"
                  : "Export",
              className: "btn_secondary",
              icon: export_icon,
              spinner: exportingPayrollId === payrollHeaders[0]?.id,
              onClick: async () => {
                if (payrollHeaders.length > 0) {
                  try {
                    await dispatch(
                      exportPayrollExcel(payrollHeaders[0].id),
                    ).unwrap();
                  } catch (error) {
                    setErrorMessage(
                      "Failed to export payroll. Please try again.",
                    );
                    setShowErrorPopup(true);
                  }
                }
              },
            },
            {
              text: calculatingPayroll ? "Calculating" : "Calculate DD",
              className: `btn_primary_dark`,
              icon: calculator_money_icon,
              spinner: calculatingPayroll,
              disabled: calculatingPayroll || payrollStatus === "Paid",
              onClick: () => {
                if (!calculatingPayroll) {
                  dispatch(
                    runPayrollCalulation({
                      key: `runPayroll-${companyId}`,
                      params: {
                        companyId,
                        payrollFrom: toDateOnly(startDate),
                        payrollTo: toDateOnly(endDate),
                      },
                    }),
                  );
                }
              },
            },
            payButton,
            ...((role === "SuperAdmin" || role === "Admin") &&
            payrollStatus === "Paid"
              ? [
                  {
                    text: "Pay Tax",
                    className: "btn_primary",
                    icon: taxpay,
                    onClick: () => setShowTaxModal(true),
                  },
                ]
              : []),
          ]}
        />
      )}

      <div className="container-fluid mt_20">
        <div className="d-flex justify-content-between align-items-center top_headding filter_row">
          <div className="datepicker position-relative">
            <DatePicker
              selectsRange
              startDate={startDate}
              endDate={endDate}
              onChange={(update) => setDateRange(update)}
              dateFormat="MM/dd/yyyy"
              className="form-control"
            />
            <img
              src={calendar_icon}
              className="datepicker_icon"
              alt="calendar"
            />
          </div>

          <FilterTopBar
            title="Employees"
            search={search}
            onSearchChange={setSearch}
            placeholder="Search Employees"
            startDate={startDate}
            endDate={endDate}
            onDateChange={handleDateChange}
            onFilterChange={setFilters}
            showExport={showExportButton}
            disableExport={employees.length === 0}
            customExportLoading={exportingPayrollId === payrollHeaders[0]?.id}
            customExportHandler={async () => {
              if (payrollHeaders.length > 0) {
                try {
                  await dispatch(
                    exportPayrollExcel(payrollHeaders[0].id),
                  ).unwrap();
                } catch (error) {
                  setErrorMessage(
                    "Failed to export payroll. Please try again.",
                  );
                  setShowErrorPopup(true);
                }
              }
            }}
            isPayrollPage={true}
          />
        </div>
      </div>

      {/* ---------- TABLE ---------- */}
      <div className="employee_table">
        <div className="container-fluid">
          <div className="white_box px_20 py_20 mt_20 border-0">
            {loading ? (
              <div className="text-center p-4">
                <div className="spinner-border" role="status">
                  <span className="visually-hidden">Loading</span>
                </div>
              </div>
            ) : (
              <EmployeePayrollTable
                employees={employees}
                headerId={payrollHeaders?.[0]?.id}
                onUpdateWorkDetails={handleUpdateWorkDetails}
                onGenerate={handleGeneratePaycheck}
                loadingId={generatingPaycheckId}
                updatingEmployeeIds={updatingEmployeeIds}
                showErrorPopup={showErrorPopup}
                errorMessage={errorMessage}
                closeErrorPopup={() => setShowErrorPopup(false)}
                page={page}
                pageSize={pageSize}
                totalRecords={totalRecords}
                onPageChange={handlePageChange}
                canGenerate={payrollHeaders?.[0]?.status === "Paid"}
              />
            )}
          </div>
        </div>
      </div>

      <DDConfirmationPopup
        show={showPayrollModal}
        onHide={handlePayrollModalClose}
      />

      <TaxPaymentModal
        show={showTaxModal}
        onHide={() => setShowTaxModal(false)}
        payrollHeaderId={payrollHeaders[0]?.id}
        companyId={companyId}
        payrollFrom={payrollHeaders[0]?.payrollFrom}
        payrollTo={payrollHeaders[0]?.payrollTo}
        companyTaxScheduleType={company?.taxScheduleType}
      />
    </div>
  );
};

export default EmployePayroll;
